import React from "react";
import { Divider, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { IStats } from "../../shared/types";
import {
  formatPairPrice,
  formatSignedPercent,
  formatSignedPp,
  formatSignedUsd,
} from "../../utils/formatters";
import EmojiEventsRoundedIcon from "@mui/icons-material/EmojiEventsRounded";
import DetailItem from "../../components/cards/subcomponents/DetailItem";
import { useAuth } from "../../hooks/useAuth";
import { SurfaceCard } from "../../components/ui/SurfaceCard";
import { Metric } from "../../components/ui/Metric";
import { capitalBasis, hasReturnBasis, hodlBenchmarkLabel, hodlReturn, returnPct } from "../../utils/botMetrics";

interface LPVsHodlCardProps {
  stats: IStats;
  decimals: number;
}

const LPVsHodlCard: React.FC<LPVsHodlCardProps> = ({ stats }) => {
  const theme = useTheme();
  const { groups } = useAuth();
  const isAdmin = groups.includes("admin");

  // The backend's LP − HODL difference over the capital basis (pair-price fallback for old payloads).
  const benchmarkReturn = hodlReturn(stats);
  const vsHodlPnl = Number.isFinite(stats.hodlDiffUSD)
    ? returnPct(stats.hodlDiffUSD as number, capitalBasis(stats))
    : stats.pnlPerCapital - benchmarkReturn;
  const vsHodlAmountUSD = stats.hodlDiffUSD ?? vsHodlPnl * capitalBasis(stats);

  if (!isAdmin) {
    return null;
  }

  const signColor = (v: number) =>
    v > 0 ? theme.palette.success.main : v < 0 ? theme.palette.error.main : undefined;

  return (
    <SurfaceCard tone={vsHodlPnl >= 0 ? "positive" : "negative"} sx={{ height: "100%" }}>
      <Metric
        icon={EmojiEventsRoundedIcon}
        label="LP vs HODL"
        value={hasReturnBasis(stats) ? formatSignedPp(vsHodlPnl) : "—"}
        sub={formatSignedUsd(vsHodlAmountUSD)}
        tone="auto"
        signal={vsHodlPnl}
        size="xl"
        inlineSub
      />
      <Divider sx={{ my: 2 }} />
      <Stack spacing={0.25}>
        <DetailItem
          label="Initial price"
          value={formatPairPrice(stats.initialPrice, stats.token0Symbol, stats.token1Symbol, stats.decimals)}
        />
        <DetailItem
          label="Current price"
          value={formatPairPrice(stats.currentPrice, stats.token0Symbol, stats.token1Symbol, stats.decimals)}
        />
        <DetailItem
          label="LP return"
          value={hasReturnBasis(stats) ? formatSignedPercent(stats.pnlPerCapital) : "—"}
          valueColor={signColor(stats.pnlPerCapital)}
        />
        <DetailItem
          label={`HODL (${hodlBenchmarkLabel([stats])})`}
          value={formatSignedPercent(benchmarkReturn)}
          valueColor={signColor(benchmarkReturn)}
        />
      </Stack>
    </SurfaceCard>
  );
};

export default LPVsHodlCard;
